import React, { useContext, useState, useEffect } from 'react';
// import Router from 'next/router';
import { Router } from '@com/core/common/translations/i18n';
import { AxiosError } from 'axios';
import { TCartType, TProductCart } from '@com/core/common/types/cart';
import { TCart } from '@com/core/common/types/cartDaily';
import { cartDailySVC as Cart } from '@com/core/common/services/api';
import { reportError } from '@com/core/common/utils/sentry';
import { AppContext } from '@com/core/common/contexts/appContext';
import { NavContext } from '@com/core/common/contexts/navContext';
import { DailyContext } from '@com/core/common/contexts/dailyContext';
import { TCity } from '@com/core/common/types/locale';
import Analytics from '@com/core/common/utils/analytics';
import { toUSD } from '@com/core/common/utils/currency';
import { useRouter } from 'next/router';
import { TBannerModule } from '../types/catalog';

type TProps = {
    children: JSX.Element;
};

type TCartCMSType = {
    top?: TBannerModule;
    midHigh?: TBannerModule;
    midLow?: TBannerModule;
    bottom?: TBannerModule;
};

export type TCartContextFunctions = {
    addAttachmentsToCart: (offerCode: string, attachments: string[]) => void;
    addAddonsToCart: (offerCode: string) => void;
    addToCart: (
        offerCode: string,
        quantity: number,
        sku: string,
        showModal: boolean,
        eventLocation?: string,
        attachments?: string[],
        isPageCart?: boolean,
    ) => void;
    addToWishlist: (offerCode: string, sku: string) => void;
    isInWishlist: (offerCode: string) => boolean;
    applyCoupon: (code: string) => void;
    deleteCoupon: () => void;
    getCart: (isPageCart: boolean) => void;
    moveItem: (offerId: string, cartType: TCartType, sku: string) => void;
    removeItem: (offerId: string, cartType: TCartType, sku: string) => void;
    removeItemInSlot: (offerId: string, dailyDeliverySlotGroupIx: number, sku: string) => void;
    updateQuantity: (offerId: string, quantity: number) => void;
    updateQuantityInSlot: (offerId: string, quantity: number, dailyDeliverySlotGroupIx: number) => void;
    setDestination: (cityId: number) => void;
    setIsCartSucceeded: (isSucceeded: boolean) => void;
    getSelectedDestination: () => TCity;
    closeQuickCart: () => void;
    isAdded: (offerCode: string) => boolean;
    getItemQty: (offerCode: string) => number;
    getItemMaxQty: (offerCode: string) => number;
    switchExperience: (experience: string) => void;
    setShippingPreference: (shippingPreference: { [code: string]: boolean }) => void;
    setPreference: (preference: string, value: string) => void;
    changeDeliverySlot: (groupIx: number, data: { deliveryTimeSlotCode: string; deliveryDate: string }) => void;
    placeOrder: (paymentId: number | string, isCOD: boolean, requestHeaders) => void;
    getPlaceOrderStatus: (orderNumber: string, cvv?: any) => Promise<void>;
    changePaymentCvv: (cvv: string) => void;
};

type TCartContextState = {
    cart: TCart | null;
    isAddToCartLoading: boolean;
    isCartLoading: boolean;
    isCartSucceeded: boolean;
    isDiscountLoading: boolean;
    isQuickCartOpen: boolean;
    isCityLoading: boolean;
    cartError: AxiosError | null;
    cmsData: TCartCMSType;
    orderStatusData: any;
    paymentCvv: string;
};

const initContextState: TCartContextState & TCartContextFunctions = {
    cart: null,
    cartError: null,
    isAddToCartLoading: false,
    isCartLoading: true,
    isCartSucceeded: false,
    isCityLoading: false,
    isDiscountLoading: false,
    isQuickCartOpen: false,
    cmsData: {},
    orderStatusData: null,
    paymentCvv: '',
    addAttachmentsToCart: async (): Promise<void> => undefined,
    addAddonsToCart: async (): Promise<void> => undefined,
    addToCart: async (): Promise<void> => undefined,
    addToWishlist: async (): Promise<void> => undefined,
    isInWishlist: (): boolean => false,
    applyCoupon: async (): Promise<void> => undefined,
    deleteCoupon: async (): Promise<void> => undefined,
    getCart: async (): Promise<void> => undefined,
    moveItem: async (): Promise<void> => undefined,
    removeItem: async (): Promise<void> => undefined,
    removeItemInSlot: async (): Promise<void> => undefined,
    updateQuantity: async (): Promise<void> => undefined,
    updateQuantityInSlot: async (): Promise<void> => undefined,
    setDestination: async (): Promise<void> => undefined,
    getSelectedDestination: (): TCity => ({} as TCity),
    setIsCartSucceeded: (): void => undefined,
    closeQuickCart: () => undefined,
    isAdded: (): boolean => false,
    getItemQty: (): number => 0,
    getItemMaxQty: (): number => 0,
    switchExperience: async (): Promise<void> => undefined,
    setShippingPreference: async (): Promise<void> => undefined,
    setPreference: async (): Promise<void> => undefined,
    changeDeliverySlot: async (): Promise<void> => undefined,
    placeOrder: async (): Promise<void> => undefined,
    getPlaceOrderStatus: async (): Promise<void> => undefined,
    changePaymentCvv: (): string => '',
};

// eslint-disable-next-linevariable-name
export const CartContext = React.createContext(initContextState);

// eslint-disable-next-linevariable-name
export const CartContextProvider = ({ children }: TProps): React.ReactElement => {
    const [cart, setCart] = useState<TCart | null>(null);
    const [cartError, setCartError] = useState<AxiosError | null>(null);
    const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);
    const [isCartLoading, setIsCartLoading] = useState<boolean>(false);
    const [isCartSucceeded, setIsCartSucceeded] = useState<boolean>(false);
    const [isCityLoading, setIsCityLoading] = useState<boolean>(false);
    const [isDiscountLoading, setIsDiscountLoading] = useState<boolean>(false);
    const [isQuickCartOpen, setIsQuickCartOpen] = useState<boolean>(false);
    const [paymentCvv, setPaymentCvv] = useState<string>('');

    const [orderStatusData, setOrderStatusData] = useState<any>(null);

    const { locale, webConfig } = useContext(AppContext);
    const { isLoading: isPageLoading } = useContext(NavContext);
    const { canEnterDaily, isLoadingCanEnterDaily } = useContext(DailyContext);

    // if page is loading, force close quick cart
    useEffect(() => {
        setIsQuickCartOpen(false);
        // reset cart success and atc state to stop atc button animation
        setIsCartSucceeded(false);
    }, [isPageLoading]);

    const router = useRouter();

    const getCart = async (isPageCart = false): Promise<void> => {
        setIsCartLoading(true);
        try {
            const response = await Cart(locale.apiLocale).getCart(isPageCart);
            setCart(response.data);
            setIsCartLoading(false);
        } catch (error) {
            setIsCartLoading(false);
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const isCartPage = router?.route === '/cart';
    useEffect(() => {
        if (!isLoadingCanEnterDaily && canEnterDaily && !isCartPage) {
            getCart(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCartPage, isLoadingCanEnterDaily, canEnterDaily]);

    const addAttachmentsToCart = async (offerCode: string, attachments: string[]): Promise<void> => {
        try {
            const response = await Cart(locale.apiLocale).postAttachments(offerCode, attachments);
            setCart(response.data);
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.addAttachmentsToCart' });
        }
    };

    const addAddonsToCart = async (offerCodes: string): Promise<void> => {
        try {
            const response = await Cart(locale.apiLocale).postFbtItems(offerCodes);
            setCart(response.data);
        } catch (error) {
            setCartError(error);

            reportError(error, { componentStack: 'cartContext.addAddonsToCart' });
        }
    };

    const addToCart = async (
        offerCode: string,
        quantity: number,
        sku: string,
        showModal: boolean,
        eventLocation?: string,
        attachments?: string[],
        isPageCart?: boolean,
    ): Promise<void> => {
        setIsAddToCartLoading(true);
        setIsCartSucceeded(false);
        try {
            const response = await Cart(locale.apiLocale).postToCart(offerCode, { quantity, attachments }, isPageCart);
            setCart({ ...cart, ...response.data } as TCart);
            const updatedCart = response && response.data;
            setIsQuickCartOpen(showModal);
            setIsAddToCartLoading(false);
            setIsCartSucceeded(true);
            Analytics.trackAddToCart(locale, {
                event: 'add_to_cart',
                eventAction: offerCode,
                eventLabel: sku,
                offerId: offerCode,
                quantity,
                cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.subtotal) || 0),
                eventLocation,
            });
        } catch (error) {
            setIsAddToCartLoading(false);
            setCartError(error);
            setIsCartSucceeded(false);
            reportError(error, { componentStack: 'cartContext.addToCart' });
        }
    };

    const closeQuickCart = (): void => {
        setIsQuickCartOpen(false);
    };

    const addToWishlist = async (offerCode: string, sku: string): Promise<void> => {
        try {
            const response = await Cart(locale.apiLocale).postSavedCart(offerCode);
            setCart(response.data);
            Analytics.trackAddToCart(locale, {
                event: 'add_to_wishlist',
                eventAction: offerCode,
                eventLabel: sku,
                offerId: offerCode,
                quantity: 1,
            });
        } catch (error) {
            setCartError(error);

            reportError(error, { componentStack: 'cartContext.addToWishlist' });
        }
    };

    const isInWishlist = (offerCode: string): boolean => {
        if (cart) {
            return !!cart.savedcart.find(item => {
                return 'offerCode' in item && item.offerCode === offerCode;
            });
        }
        return false;
    };

    const applyCoupon = async (code: string): Promise<void> => {
        setIsDiscountLoading(true);

        try {
            const response = await Cart(locale.apiLocale).postCoupon(code);
            setCart({ ...cart, ...response.data });
            setIsDiscountLoading(false);
        } catch (error) {
            setIsDiscountLoading(false);

            reportError(error, { componentStack: 'cartContext.applyCoupon' });
        }
    };

    const deleteCoupon = async (): Promise<void> => {
        setIsDiscountLoading(true);

        try {
            const response = await Cart(locale.apiLocale).deleteCoupon();
            setCart({ ...cart, ...response.data });
            setIsDiscountLoading(false);
        } catch (error) {
            setIsDiscountLoading(false);

            reportError(error, { componentStack: 'cartContext.deleteCoupon' });
        }
    };

    // THIS ISN'T USED?
    const moveItem = async (offerId: string, cartType: TCartType, sku: string): Promise<void> => {
        setIsCartLoading(true);

        try {
            const response = await Cart(locale.apiLocale).postMove(locale.isoLocale, offerId, cartType, sku);
            setCart(response.data);
            setIsCartLoading(false);
            Analytics.trackAddToCart(locale, {
                event: cartType === 'savedcart' ? 'add_to_wishlist' : 'add_to_cart',
                eventAction: offerId,
                eventLabel: sku,
                offerId,
                skuConfig: sku,
                cartItemsTotal: cart?.cartCount || 0,
                cartValueTotal: toUSD(locale.isoLocale, cart?.invoice.total || 0),
            });

            Analytics.trackRemoveFromCart(locale, {
                event: cartType === 'savedcart' ? 'remove_from_wishlist' : 'remove_from_cart',
                eventAction: offerId,
                eventLabel: sku,
                offerId,
                skuConfig: sku,
                cartItemsTotal: cart?.cartCount || 0,
                cartValueTotal: toUSD(locale.isoLocale, cart?.invoice.total || 0),
            });
        } catch (error) {
            setIsCartLoading(false);
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const removeItem = async (offerId: string, cartType: TCartType, sku: string): Promise<void> => {
        setIsCartLoading(true);
        try {
            const response = await Cart(locale.apiLocale).remove(offerId, cartType);
            setCart(response.data);
            setIsCartLoading(false);
            Analytics.trackRemoveFromCart(locale, {
                event: cartType === 'savedcart' ? 'remove_from_wishlist' : 'remove_from_cart',
                eventAction: offerId,
                eventLabel: sku,
                offerId,
                cartItemsTotal: cart?.cartCount || 0,
                cartValueTotal: toUSD(locale.isoLocale, cart?.invoice.subtotal || 0),
            });
        } catch (error) {
            setIsCartLoading(false);

            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const removeItemInSlot = async (offerId: string, dailyDeliverySlotGroupIx: number, sku: string): Promise<void> => {
        setIsCartLoading(true);
        try {
            const response = await Cart(locale.apiLocale).removeInSlot(offerId, dailyDeliverySlotGroupIx);
            setCart(response.data);
            setIsCartLoading(false);
            Analytics.trackRemoveFromCart(locale, {
                event: 'remove_from_cart',
                eventAction: offerId,
                eventLabel: sku,
                offerId,
                cartItemsTotal: cart?.cartCount || 0,
                cartValueTotal: toUSD(locale.isoLocale, cart?.invoice.subtotal || 0),
            });
        } catch (error) {
            setIsCartLoading(false);

            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const updateQuantity = async (offerId: string, quantity: number): Promise<void> => {
        try {
            const product = cart?.items?.find((item: TProductCart) => item.offerCode === offerId);
            const response = await Cart(locale.apiLocale).put(offerId, quantity);
            setCart(response.data);
            const updatedCart = response && response.data;
            if (quantity > product?.quantity) {
                Analytics.trackAddToCart(locale, {
                    event: 'add_to_cart',
                    eventAction: offerId,
                    eventLabel: product?.skuConfig,
                    offerId,
                    quantity,
                    skuConfig: product?.skuConfig,
                    cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                    cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.subtotal) || 0),
                });
            } else {
                Analytics.trackRemoveFromCart(locale, {
                    event: 'remove_from_cart',
                    eventAction: offerId,
                    eventLabel: product?.skuConfig,
                    offerId,
                    skuConfig: product?.skuConfig,
                    cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                    cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.subtotal) || 0),
                });
            }
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const updateQuantityInSlot = async (
        offerId: string,
        quantity: number,
        dailyDeliverySlotGroupIx: number,
    ): Promise<void> => {
        try {
            const product = cart.items.find((item: TProductCart) => item.offerCode === offerId);
            const response = await Cart(locale.apiLocale).putQtyInSlot(offerId, quantity, dailyDeliverySlotGroupIx);
            setCart(response.data);
            const updatedCart = response && response.data;
            if (quantity > product.quantity) {
                Analytics.trackAddToCart(locale, {
                    event: 'add_to_cart',
                    eventAction: offerId,
                    eventLabel: product?.skuConfig,
                    offerId,
                    quantity,
                    skuConfig: product.skuConfig,
                    cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                    cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.subtotal) || 0),
                });
            } else {
                Analytics.trackRemoveFromCart(locale, {
                    event: 'remove_from_cart',
                    eventAction: offerId,
                    eventLabel: product.skuConfig,
                    offerId,
                    skuConfig: product.skuConfig,
                    cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                    cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.subtotal) || 0),
                });
            }
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const setDestination = async (cityId: number): Promise<void> => {
        setIsCityLoading(true);
        try {
            const response = await Cart(locale.apiLocale).postCity(cityId, '&showCart');
            setCart(response.data);
            setIsCityLoading(false);
        } catch (error) {
            setCartError(error);
            setIsCityLoading(false);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const getSelectedDestination = (): TCity => {
        let selectedDestination = {} as TCity;

        try {
            const { city } = webConfig.country;

            selectedDestination =
                city && city.filter((cityItem: TCity) => cityItem.id === (cart && cart.estimatedDeliveryCity))[0];
        } catch (error) {
            setCartError(error);

            reportError(error, { componentStack: 'cartContext.getSelectedDestination' });
        }

        return selectedDestination;
    };

    const isAdded = (offerCode: string): boolean => {
        const isInCart =
            !!cart &&
            cart.items &&
            cart.items.length > 0 &&
            cart.items.find(offer => 'offerCode' in offer && offer.offerCode === offerCode);
        return isInCart;
    };

    const getItemQty = (offerCode: string): number => {
        const currentOffer = !!cart && cart.items.find(offer => 'offerCode' in offer && offer.offerCode === offerCode);
        if (currentOffer) {
            return currentOffer.quantityTotal || currentOffer.quantity;
        }
        return 0;
    };

    const getItemMaxQty = (offerCode: string): number => {
        const currentOffer =
            !!cart && cart?.items?.find(offer => 'offerCode' in offer && offer.offerCode === offerCode);
        if (currentOffer) {
            return currentOffer.quantityMax;
        }
        return 0;
    };

    const cmsData: TCartCMSType = {};
    if (cart?.cms && cart.cms.data.length > 0) {
        cmsData.top = cart.cms.data.find(module => module.position === 'top');
        cmsData.midHigh = cart.cms.data.find(module => module.position === 'midHigh');
        cmsData.midLow = cart.cms.data.find(module => module.position === 'midLow');
        cmsData.bottom = cart.cms.data.find(module => module.position === 'bottom');
    }

    const switchExperience = async (experience: string): Promise<void> => {
        setIsCartLoading(true);
        try {
            const response = await Cart(locale.apiLocale).postExperience(experience);
            setCart(response.data);
            setIsCartLoading(false);
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.switchExperience' });
        }
    };

    const setShippingPreference = async (shippingPreference: { [code: string]: boolean }): Promise<void> => {
        setIsCartLoading(true);
        try {
            const response = await Cart(locale.apiLocale).postShippingPreference(shippingPreference);
            setCart(response.data);
            setIsCartLoading(false);
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.setShippingPreference' });
        }
    };

    const setPreference = async (preference: string, value: string): Promise<void> => {
        setIsCartLoading(true);
        try {
            const response = await Cart(locale.apiLocale).postPreference(preference, value);
            setCart(response.data);
            setIsCartLoading(false);
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.setPreference' });
        }
    };

    const changeDeliverySlot = async (
        groupIx: number,
        data: { deliveryTimeSlotCode: string; deliveryDate: string },
    ): Promise<void> => {
        setIsCartLoading(true);
        try {
            const response = await Cart(locale.apiLocale).postDeliverySlotChange(groupIx, data);
            setCart(response.data);
            setIsCartLoading(false);
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.changeDeliverySlot' });
        }
    };

    // ---------------------------------------- PLACE MANUAL ORDER
    const placeOrder = async (paymentId: number | string, isCOD = false, cvv?: number): Promise<void> => {
        setIsCartLoading(true);
        const cvvHeader = cvv ? { 'x-cvv': cvv } : {};
        try {
            const { data, error } = await Cart(locale.apiLocale).postOrder(isCOD ? '' : paymentId, cvvHeader);
            const { screenType } = data;

            if (!screenType || screenType === 'success') {
                placeOrderStatusHandler(data, error, cvv);
            } else if (screenType === 'resolution') {
                // go to resolution - something went out of stock or the delivery slot is no longer available
            } else {
                // cli todo error handing
                alert('Something went wrong');
                setIsCartLoading(false);
            }
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.placeOrder' });
            setIsCartLoading(false);
        }
    };

    const placeOrderStatusHandler = (data, error, cvv) => {
        switch (data.status) {
            case 'confirmed':
            case 'payment_processing':
                setOrderStatusData(data);
                setIsCartLoading(false);
                Analytics.trackTransaction(locale, {
                    event: 'transaction',
                    transactionId: data.orderNr,
                    transactionCurrency: locale.currencyCodeEN,
                    transactionTotal: (data && data.invoice && data.invoice.subtotal) || 0,
                    criteoTransactionProducts: Analytics.formatCriteoCartItems(data),
                    fb_content: Analytics.formatFacebookItems(data),
                    fb_content_id: Analytics.formatCartItems(data),
                });
                Router.push(`/payment?order=${data.orderNr}`);
                if (data.status === 'payment_processing') {
                    getProcessingOrderStatus(data);
                }
                break;
            case 'auth_required':
                try {
                    window.location.href = data.redirect;
                } catch (err) {
                    // cli todo error handling here please
                    alert('something went wrong' || err);
                }
                break;
            case 'payment_failed':
            case 'error':
                // cli todo error handling here please
                alert(error);
                break;
            case 'initiated':
                getPlaceOrderStatus(data.orderNr, cvv);
                break;
            case 'payment_pending':
                setTimeout(() => getPlaceOrderStatus(data.orderNr, cvv), 1000);
                break;
            default:
                // cli todo error handling here please
                alert('something went wrong');
        }
    };

    const getPlaceOrderStatus = async (orderNumber: string, cvv?: any) => {
        const cvvHeader = cvv ? { 'x-cvv': cvv } : {};
        try {
            const { data, error } = await Cart(locale.apiLocale).getPlaceOrderStatus(orderNumber, cvvHeader);
            placeOrderStatusHandler(data, error, cvv);
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getPlaceOrderStatus' });
        }
    };

    // ---------------------------------------- SUCCESS PAGE CHECK - CHECKING payment_processing > confirmed
    const getProcessingOrderStatus = async placedOrder => {
        try {
            const { data } = await Cart(locale.apiLocale).getProcessingOrderStatus(placedOrder.orderNr);
            switch (data.status) {
                case 'payment_processing':
                    getProcessingOrderStatus(placedOrder);
                    break;
                // cli to do - maybe just update the two things instead?
                default:
                    setOrderStatusData({
                        ...placedOrder,
                        status: data.status,
                        message: data.message,
                    });
            }
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getProcessingOrderStatus' });
            setTimeout(() => getProcessingOrderStatus(placedOrder), 1000);
        }
    };

    const changePaymentCvv = (cvv: string) => {
        setPaymentCvv(cvv);
    };

    const cartContextValue = {
        // ------state--------
        cart,
        cartError,
        isAddToCartLoading,
        isCartLoading,
        isCartSucceeded,
        isQuickCartOpen,
        isCityLoading,
        isDiscountLoading,
        cmsData,
        orderStatusData,
        paymentCvv,
        // -----functions-----
        addAttachmentsToCart,
        addAddonsToCart,
        addToCart,
        addToWishlist,
        isInWishlist,
        applyCoupon,
        deleteCoupon,
        getCart,
        moveItem,
        removeItem,
        removeItemInSlot,
        updateQuantity,
        updateQuantityInSlot,
        setDestination,
        getSelectedDestination,
        setIsCartSucceeded,
        closeQuickCart,
        isAdded,
        getItemQty,
        getItemMaxQty,
        switchExperience,
        setShippingPreference,
        setPreference,
        changeDeliverySlot,
        placeOrder,
        getPlaceOrderStatus,
        changePaymentCvv,
    };

    return <CartContext.Provider value={cartContextValue}>{children}</CartContext.Provider>;
};
